import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

interface InfiniteHorizontalScrollProps<T> {
  items: T[];
  renderItem: (item: T, index: number, isAtLeftMost: boolean) => React.ReactNode;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isInfiniteScroll?: boolean;
  scrollAmount?: number;
  containerClassName?: string;
  alignCenterIfLessOrEqual?: number;
  sliderRef?: React.RefObject<HTMLDivElement | null>;
}

export function InfiniteHorizontalScroll<T>({
  items,
  renderItem,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  isInfiniteScroll = false,
  scrollAmount = 270,
  containerClassName,
  alignCenterIfLessOrEqual,
  sliderRef,
}: InfiniteHorizontalScrollProps<T>) {
  const localSliderRef = useRef<HTMLDivElement>(null);
  const activeSliderRef = sliderRef || localSliderRef;
  const sentinelRef = useRef<HTMLDivElement>(null);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isAtLeftMost, setIsAtLeftMost] = useState(true);

  const updateArrowVisibility = useCallback(() => {
    const slider = activeSliderRef.current;
    if (!slider) return;
    const { scrollLeft, scrollWidth, clientWidth } = slider;
    setIsAtLeftMost(scrollLeft <= 1);
    setShowLeftArrow(scrollLeft > 1);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSliderRef.current]);

  useEffect(() => {
    const slider = activeSliderRef.current;
    if (!slider) return;

    updateArrowVisibility();

    const handleScroll = () => {
      updateArrowVisibility();
    };

    slider.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    const timer = setTimeout(updateArrowVisibility, 300);

    return () => {
      slider.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      clearTimeout(timer);
    };
  }, [items, activeSliderRef, updateArrowVisibility]);

  useEffect(() => {
    if (!isInfiniteScroll || !onLoadMore || !hasMore || isLoadingMore) return;
    const sentinel = sentinelRef.current;
    const slider = activeSliderRef.current;
    if (!sentinel || !slider) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: slider,
        rootMargin: '0px 800px 0px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [isInfiniteScroll, hasMore, isLoadingMore, onLoadMore, activeSliderRef]);

  const handleScrollClick = (direction: 'left' | 'right') => {
    const slider = activeSliderRef.current;
    if (!slider) return;
    const offset = direction === 'left' ? -scrollAmount : scrollAmount;
    slider.scrollBy({ left: offset, behavior: 'smooth' });
  };

  const shouldCenter = alignCenterIfLessOrEqual != null && items.length <= alignCenterIfLessOrEqual;

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={() => handleScrollClick('left')}
        className={cn(
          "absolute left-2 top-1/2 z-20 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-[0px_4px_12px_rgba(0,0,0,0.15)] hover:bg-[#011E39] hover:text-white transition-all duration-200",
          !showLeftArrow && "pointer-events-none opacity-0"
        )}
        aria-label="Scroll left"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      <div
        ref={activeSliderRef}
        className={cn(
          "flex w-full gap-3 overflow-x-auto pb-4 scroll-smooth scrollbar-none snap-x touch-pan-x",
          shouldCenter && "justify-center",
          containerClassName
        )}
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((item, idx) => renderItem(item, idx, isAtLeftMost))}

        {isInfiniteScroll && hasMore && (
          <div
            ref={sentinelRef}
            className="w-1 shrink-0 h-full self-stretch"
            aria-hidden="true"
          />
        )}
      </div>

      <button
        type="button"
        onClick={() => handleScrollClick('right')}
        className={cn(
          "absolute right-2 top-1/2 z-20 -translate-y-1/2 flex items-center justify-center w-10 h-11 rounded-full bg-white shadow-[0px_4px_12px_rgba(0,0,0,0.15)] hover:bg-[#011E39] hover:text-white transition-all duration-200",
          !showRightArrow && "pointer-events-none opacity-0"
        )}
        aria-label="Scroll right"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
